import type { ReactElement } from 'react';
import { Mic } from 'lucide-react';
import { Card } from '@/components/atoms/card';
import { cn } from '@/lib/cn';

const PRIVACY_BULLETS: readonly string[] = [
  'Audio stays on your device until you save a take.',
  'Saved takes are private to your account.',
  'You can revoke microphone access in your browser settings at any time.',
];

export type MicPermissionCardProps = {
  onAllow: () => void;
  onDismiss?: () => void;
};

export function MicPermissionCard({ onAllow, onDismiss }: MicPermissionCardProps): ReactElement {
  return (
    <Card padding="lg" className="max-w-md">
      <div className="mb-4 flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-soft)] text-[var(--ink)]">
          <Mic className="h-5 w-5" />
        </span>
        <h2 className="text-lg font-semibold text-[var(--ink)]">Allow microphone</h2>
      </div>
      <ul className="mb-6 space-y-2 text-sm text-[var(--ink-2)]">
        {PRIVACY_BULLETS.map((bullet) => (
          <li key={bullet} className="flex gap-2">
            <span aria-hidden="true" className="mt-2 inline-block h-1 w-1 rounded-full bg-[var(--ink-3)]" />
            <span>{bullet}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-end gap-2">
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className={cn(
              'inline-flex h-10 items-center justify-center rounded-[var(--r-sm)] px-4 text-sm font-semibold',
              'bg-[var(--bg-soft)] text-[var(--ink)] hover:bg-[var(--rule)]',
            )}
          >
            Not now
          </button>
        )}
        <button
          type="button"
          onClick={onAllow}
          className={cn(
            'inline-flex h-10 items-center justify-center rounded-[var(--r-sm)] px-4 text-sm font-semibold',
            'bg-[var(--ink)] text-[var(--bg-surface)] hover:bg-[var(--brand-ink)]',
          )}
        >
          Allow microphone
        </button>
      </div>
    </Card>
  );
}
